import React, { useEffect, useState } from "react";

import { FieldSchema, ResourceSchema } from "../types";
import ValidatorRegistry from "../utils/ValidatorRegistry";
import descriptors, { ResourceName } from "../descriptors";
import { useHeimdallContext } from "../HeimdallContext";
import {
    useForm,
    SubmitHandler,
    Controller,
    ResolverOptions,
    FieldError,
    ControllerRenderProps,
    FieldErrors,
    Control,
    FieldValues,
} from "react-hook-form";
import { CRUDAction, ResourceDescriptor, SelectOption } from "../descriptors/common";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormHelperText,
    ListItemText,
    MenuItem,
    Select,
    Switch,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { FetchFunction, useFetch } from "./FetchProvider";
import { displayError } from "../utils/form";
import Loading from "./Loading";
import { fetchList, fetchLists, ListRequest } from "../utils/crud";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import StringListEditor from "./StringListEditor";
import DurationField from "./DurationField";
import { useNotification } from "./NotificationProvider";

function getFieldNames(resourceSchema: ResourceSchema, descriptor: ResourceDescriptor, action: CRUDAction) {
    const orders = descriptor.orders;
    const order = action in orders ? orders[action] : null;
    if (order == null) return Object.keys(resourceSchema).filter((key) => key != "id");
    return order;
}

const crudResolver =
    (schema: ResourceSchema, validatorRegistry: ValidatorRegistry, formatMessage: IntlShape["formatMessage"]) =>
    async (values: any, _context: any, _options: ResolverOptions<any>) => {
        const errors: Record<string, FieldError> = {};
        for (const [fieldName, value] of Object.entries(values)) {
            const fieldSchema = schema[fieldName];
            if (fieldSchema === undefined) {
                errors[fieldName] = {
                    type: "unknown",
                    message: "Unknown field",
                    // types: {},
                };
            } else {
                for (const { name, key, params } of fieldSchema.validators) {
                    const validatorArgs = [value, key, params === undefined ? null : params, values];
                    const result = validatorRegistry.run(name, validatorArgs);
                    if (Object.entries(result).length > 0) {
                        // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
                        const msg = formatMessage({ id: result.key }, result);
                        if (!errors[fieldName]) {
                            errors[fieldName] = {
                                type: name,
                                message: msg,
                                types: { [name]: msg },
                            };
                        } else {
                            // add new error type
                            errors[fieldName].types = {
                                ...errors[fieldName].types,
                                [name]: msg,
                            };
                        }
                    }
                }
            }
        }
        return { values: values, errors };
    };

function applyRelationFormatting(resourceName: ResourceName, rows: any[]) {
    const descriptor = descriptors[resourceName];
    if (!descriptor.optionsFormatter) {
        throw new Error(`No option formatter for ${resourceName}`);
    }
    return rows.map((row) => descriptor.optionsFormatter(row));
}

/// issues list request for all the relations and returns a mapping of field name to list of rows
async function collectRelationData(
    fetch: FetchFunction,
    fieldNames: string[],
    schema: ResourceSchema,
): Promise<Record<string, SelectOption[]>> {
    const requests: [string, ListRequest][] = [];
    for (const fieldName of fieldNames) {
        const fieldSchema = schema[fieldName];
        if (fieldSchema.type.name === "relation") {
            requests.push([fieldName, new ListRequest(fieldSchema.type.target)]);
        }
    }
    if (requests.length == 0) return {};

    function targetResourceName(fieldName: string): ResourceName {
        return schema[fieldName].type.target as ResourceName;
    }

    // TODO
    // this._context.postProcessRequests(requests);

    if (requests.length === 1) {
        const result = await fetchList(fetch, requests[0][1]);
        return {
            [requests[0][0]]: applyRelationFormatting(targetResourceName(requests[0][0]), result.rows),
        };
    }

    const result = await fetchLists(
        fetch,
        requests.map(([_name, request]) => request),
    );
    // collect requests and result into a dictionary
    return Object.fromEntries(
        requests.map(([name], index) => [name, applyRelationFormatting(targetResourceName(name), result[index].rows)]),
    );
}

async function fetchInitialData(fetch: FetchFunction, resourceName: ResourceName, id: number) {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    const response = await fetch(`/api/${resourceName}/${id}`, {
        method: "GET",
        headers: headers,
    });

    if (response.ok) {
        const body = await response.json();
        return body.data;
    }
    return null;
}

function renderTextField(field: ControllerRenderProps, schema: FieldSchema, errors: Record<string, FieldError>) {
    return (
        <TextField
            {...field}
            size="small"
            fullWidth
            error={!!errors[schema.name]}
            helperText={errors[schema.name] ? displayError(errors[schema.name]) : null}
        />
    );
}

function renderDurationField(field: ControllerRenderProps, schema: FieldSchema, errors: Record<string, FieldError>) {
    return (
        <DurationField
            {...field}
            size="small"
            error={!!errors[schema.name]}
            helperText={errors[schema.name] ? displayError(errors[schema.name]) : null}
        />
    );
}

function renderIntegerField(field: ControllerRenderProps, schema: FieldSchema, errors: Record<string, FieldError>) {
    return (
        <TextField
            {...field}
            type="number"
            slotProps={{
                input: {
                    inputProps: { step: 1 },
                },
            }}
            size="small"
            fullWidth
            error={!!errors[schema.name]}
            helperText={errors[schema.name] ? displayError(errors[schema.name]) : null}
        />
    );
}

function renderBooleanField(field: ControllerRenderProps, schema: FieldSchema, errors: Record<string, FieldError>) {
    return (
        <FormControl error={!!errors[schema.name]} fullWidth>
            <Switch
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                name={field.name}
            />
            {errors[schema.name] && <FormHelperText>{displayError(errors[schema.name])}</FormHelperText>}
        </FormControl>
    );
}

function renderArrayField(
    { value, onChange }: ControllerRenderProps,
    schema: FieldSchema,
    errors: Record<string, FieldError>,
) {
    // XXX: when 'null' is returned from server, for some reason value becomes ''
    if (value === "") value = [];

    return (
        <FormControl error={!!errors[schema.name]} fullWidth>
            <StringListEditor value={value} onChange={onChange} />
            {errors[schema.name] && <FormHelperText>{displayError(errors[schema.name])}</FormHelperText>}
        </FormControl>
    );
}

function renderRelationField(
    field: ControllerRenderProps,
    schema: FieldSchema,
    errors: Record<string, FieldError>,
    options: SelectOption[],
) {
    return (
        <Select
            {...field}
            value={field.value === null ? "not-set" : field.value}
            onChange={(event) => field.onChange(event.target.value === "not-set" ? null : event.target.value)}
            displayEmpty
            size="small"
            fullWidth
            error={!!errors[schema.name]}
            // no helperText here - but validation error probably does not make sense for a relation
        >
            {!schema.required && (
                <MenuItem value={"not-set"}>
                    <em>
                        <FormattedMessage id="not-set" defaultMessage="Not set" />
                    </em>
                </MenuItem>
            )}
            {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.text}
                </MenuItem>
            ))}
        </Select>
    );
}

function renderEnumField(
    field: ControllerRenderProps,
    schema: FieldSchema,
    errors: FieldErrors<any>,
    valueTranslationKey: (value: string) => string,
) {
    return (
        <Select
            {...field}
            value={field.value === null ? "not-set" : field.value}
            onChange={(event) => field.onChange(event.target.value === "not-set" ? null : event.target.value)}
            displayEmpty
            size="small"
            fullWidth
            error={!!errors[schema.name]}
            disabled={field.disabled}
        >
            {!schema.required && (
                <MenuItem value={"not-set"}>
                    <em>
                        <FormattedMessage id="not-set" defaultMessage="Not set" />
                    </em>
                </MenuItem>
            )}
            {schema.type.options.map((option: string) => {
                if (valueTranslationKey) {
                    return (
                        <MenuItem key={option} value={option}>
                            {/* eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id */}
                            <FormattedMessage id={valueTranslationKey(option)} />
                        </MenuItem>
                    );
                } else {
                    return (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    );
                }
            })}
        </Select>
    );
}

function renderArrayOfEnumField(
    field: ControllerRenderProps,
    schema: FieldSchema,
    errors: FieldErrors<any>,
    valueTranslationKey?: (value: string) => string,
) {
    // XXX: when 'null' is returned from server, for some reason value becomes ''
    const value: string[] = Array.isArray(field.value) ? field.value : [];
    const optionLabel = (option: string) =>
        // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
        valueTranslationKey ? <FormattedMessage id={valueTranslationKey(option)} /> : option;

    return (
        <FormControl error={!!errors[schema.name]} fullWidth>
            <Select
                {...field}
                multiple
                value={value}
                // keep the order of the options, not the order of clicking
                onChange={(event) => {
                    const selected = event.target.value as string[];
                    field.onChange(schema.type.options.filter((option: string) => selected.includes(option)));
                }}
                renderValue={(selected: string[]) =>
                    selected.map((option, index) => (
                        <React.Fragment key={option}>
                            {index > 0 && ", "}
                            {optionLabel(option)}
                        </React.Fragment>
                    ))
                }
                size="small"
                disabled={field.disabled}
            >
                {schema.type.options.map((option: string) => (
                    <MenuItem key={option} value={option}>
                        <Checkbox checked={value.includes(option)} size="small" />
                        <ListItemText primary={optionLabel(option)} />
                    </MenuItem>
                ))}
            </Select>
            {errors[schema.name] && <FormHelperText>{displayError(errors[schema.name] as FieldError)}</FormHelperText>}
        </FormControl>
    );
}

const apiDateFormat = "YYYY-MM-DD";

function renderDateField(
    field: ControllerRenderProps,
    _schema: FieldSchema,
    _errors: Record<string, FieldError>,
    _formatMessage: IntlShape["formatMessage"],
) {
    return (
        <DatePicker
            {...field}
            value={field.value ? moment(field.value, apiDateFormat) : null}
            onChange={(date) => {
                if (date && date.isValid()) {
                    field.onChange(moment(date).format(apiDateFormat));
                } else {
                    field.onChange(null);
                }
            }}
            slotProps={{
                textField: {
                    size: "small",
                },
            }}
        />
    );
}

const fieldTypeToRender = {
    string: renderTextField,
    String: renderTextField,
    Duration: renderDurationField,
    Integer: renderIntegerField,
    Boolean: renderBooleanField,
    Array: renderArrayField,
    relation: renderRelationField,
    Enum: renderEnumField,
    ArrayOfEnum: renderArrayOfEnumField,
    Date: renderDateField,
};

function defaultForField(schema: FieldSchema, allowNull: boolean) {
    if (schema.type.name === "Enum") {
        const def = schema.default !== undefined ? schema.default : null;
        if (def) return def;
        else return schema.required ? schema.type.options[0] : null;
    }

    if (schema.default !== undefined && schema.default !== null) return schema.default;

    if (allowNull) return null;

    if (["Date", "DateTime"].includes(schema.type.name)) {
        return null;
    }
    return "";
}

function findDefaults(fieldNames: string[], schema: ResourceSchema, allowNull: boolean = false) {
    return Object.fromEntries(fieldNames.map((key) => [key, defaultForField(schema[key], allowNull)]));
}

interface ValidationResponse {
    status: "error";
    code: 400;
    data: Record<
        string,
        {
            key: string;
            name: string;
        }[]
    >;
    resourceName: string;
}

function createFieldRows(
    fieldNames: string[],
    schema: ResourceSchema,
    resourceName: string,
    resourceDescriptor: ResourceDescriptor,
    action: CRUDAction,
    control: Control,
    errors: FieldErrors<any>,
    relationData: Record<string, any>,
    formValues: Record<string, any>,
) {
    const fields: React.JSX.Element[] = [];
    for (const fieldName of fieldNames) {
        const fieldSchema = schema[fieldName];

        const labelKey = `${resourceName}.${fieldName}`;
        const render = ({ field }: { field: ControllerRenderProps<FieldValues, string> }) => {
            const disableRule = resourceDescriptor.disableRules?.[action]?.[fieldName];
            field.disabled = disableRule ? disableRule(formValues) : false;

            const CustomRenderer = resourceDescriptor.muiRenderers?.[action]?.[fieldName];
            if (CustomRenderer) {
                const { onChange, onBlur, value, disabled, name, ref } = field;
                const forwardedProps = {
                    onChange,
                    onBlur,
                    value,
                    disabled,
                    name,
                    size: "small",
                    fullWidth: true,
                    required: fieldSchema.required,
                    error: !!errors[field.name],
                    helperText: errors[field.name] ? displayError(errors[field.name] as FieldError) : null,
                    ref:
                        typeof CustomRenderer === "object" &&
                        CustomRenderer.$$typeof === Symbol.for("react.forward_ref")
                            ? ref
                            : undefined,
                };

                return <CustomRenderer {...forwardedProps} />;
            }
            if (fieldSchema.type.name === "Enum") {
                return renderEnumField(field, fieldSchema, errors, resourceDescriptor.enumTranslation?.[fieldName]);
            }
            if (fieldSchema.type.name === "ArrayOfEnum") {
                return renderArrayOfEnumField(
                    field,
                    fieldSchema,
                    errors,
                    resourceDescriptor.enumTranslation?.[fieldName],
                );
            }

            const renderFunc = fieldTypeToRender[fieldSchema.type.name];
            if (!renderFunc) throw new Error(`No render function for ${fieldSchema.type.name}`);
            return renderFunc(field, fieldSchema, errors, relationData[fieldName]);
        };
        fields.push(
            <React.Fragment key={fieldName}>
                <Grid size={4}>
                    {/* eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id */}
                    <FormattedMessage id={labelKey} />
                </Grid>
                <Grid size={8}>
                    <Controller control={control} name={fieldName} render={render} />
                </Grid>
            </React.Fragment>,
        );
    }
    return fields;
}

export type FormActionButton = React.FC<{
    formValues: Record<string, any>;
}>;

/**
 * Reimplementation of CRUD form based on react-hook-form.
 */
const CRUDForm: React.FC<{
    resourceName: string;
    extraRequestData?: Record<string, any>;
    action: "create" | "update";
    id?: number; /// id of the resource to update
    onDirtyChange?: (isDirty: boolean) => void;
    onSuccess?: () => void;
    extraButtons?: FormActionButton[];
    formFields?: string[]; /// fields that will be put on the form - if not set, then resource descriptor will be used
    overrideSubmitURI?: string;
}> = ({
    action,
    resourceName,
    extraRequestData,
    onDirtyChange,
    onSuccess,
    id,
    extraButtons,
    formFields,
    overrideSubmitURI,
}) => {
    const { showSuccess } = useNotification();
    const { validatorRegistry } = useHeimdallContext();
    const fetch = useFetch();
    const [serverError, setServerError] = useState<string | null>(null);
    const isMountedRef = React.useRef(true);

    const schema = window.schemas.resources[resourceName];
    const resourceDescriptor = descriptors[resourceName as ResourceName];
    const currentFieldNames = formFields ? formFields : getFieldNames(schema, resourceDescriptor, action);
    // `getFieldNames` builds a new list for an action without an order, and parents pass `formFields`
    // inline: keep one array per content, or the effects below fetch again after every response.
    const fieldNamesKey = JSON.stringify(currentFieldNames);
    const fieldNames: string[] = React.useMemo(() => JSON.parse(fieldNamesKey), [fieldNamesKey]);
    const [relationData, setRelationData] = useState<Record<string, any>>(null);
    const { formatMessage } = useIntl();

    const defaultValues = React.useMemo(() => findDefaults(fieldNames, schema), [fieldNames, schema]);
    /// initial data - in case of create this will be set {} to indicate that there is no need to fetch
    const [initialData, setInitialData] = useState<any>(null);
    /// only used when tabbed orders are defined
    const [activeTab, setActiveTab] = useState(0);

    const {
        control,
        handleSubmit,
        setError,
        reset,
        watch,
        formState: { errors, isDirty },
    } = useForm({
        resolver: crudResolver(schema, validatorRegistry, formatMessage),
        defaultValues,
        mode: "onChange",
    });

    // Watch all form values for conditional logic
    const formValues = watch();

    // Report dirty state changes up to parent
    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Handle component unmounting
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        collectRelationData(fetch, fieldNames, schema).then(setRelationData);
    }, [fetch, fieldNames, schema]);

    useEffect(() => {
        if (action == "create") setInitialData({});
        else
            fetchInitialData(fetch, resourceName as ResourceName, id).then((data) => {
                let processedData = Object.fromEntries(
                    Object.entries(data).filter(([key]) => fieldNames.includes(key)),
                );

                if (resourceDescriptor.initialDataPostProcess) {
                    processedData = resourceDescriptor.initialDataPostProcess(processedData);
                }

                setInitialData(processedData);
            });
    }, [action, fetch, fieldNames, id, resourceDescriptor, resourceName]);

    const handleReset = React.useCallback(() => {
        reset({ ...defaultValues, ...initialData });
    }, [reset, defaultValues, initialData]);

    // whenever initialData changes, reset the form
    React.useEffect(() => {
        if (initialData) {
            reset({ ...defaultValues, ...initialData });
        }
    }, [initialData, reset, defaultValues]);

    const onSubmit: SubmitHandler<any> = async (data) => {
        // the old code sent all the fields not just the ones in the form
        // we will just with fields that are required
        let extendedData = data;
        if (action === "create") {
            const requiredFields = Object.entries(schema)
                .filter(([_key, field]) => field.required)
                .map(([key, _field]) => key);
            console.log("required fields", requiredFields);
            extendedData = { ...findDefaults(requiredFields, schema, true), ...data };
        }

        const url = overrideSubmitURI
            ? overrideSubmitURI
            : action === "create"
            ? `/api/${resourceName}/`
            : `/api/${resourceName}/${id}`;
        const response = await fetch(url, {
            method: action === "create" ? "POST" : "PATCH",
            headers: {
                accept: "application/json",
                "content-type": "application/json",
            },
            body: JSON.stringify({ ...extendedData, ...extraRequestData }),
        });

        if (response.ok) {
            // success notification
            // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
            const notificationName = <FormattedMessage id={`${resourceName}.notificationName`} />;
            showSuccess(
                // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
                <FormattedMessage id={`resource_${action}d`} values={{ notificationName }} />,
                <FormattedMessage id="notification.success" defaultMessage="Success" />,
            );
            if (onSuccess) onSuccess();
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                console.log("updating state");
                setInitialData(data);
                reset({ ...defaultValues, ...data });
            }
        } else if (response.status === 400) {
            const body = (await response.json()) as ValidationResponse;
            if (body.resourceName === resourceName) {
                // validation error handling
                for (const [fieldName, fieldErrors] of Object.entries(body.data)) {
                    if (fieldErrors.length > 0) {
                        // Create types object with all errors
                        const types = {};
                        // Create concatenated message with all errors
                        const messages = [];

                        // Process all errors for the field
                        for (const error of fieldErrors) {
                            // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
                            const message = formatMessage({ id: error.key });
                            types[error.name] = message;
                            messages.push(message);
                        }

                        setError(fieldName, {
                            type: "server",
                            message: messages.join(", "), // Join all messages
                            types: types, // Include all error types
                        });
                    }
                }
            } else {
                console.error("Validation response seems to be invalid");
                setServerError("unexpected_server_error");
            }
        } else {
            // other errors
            setServerError("unexpected_server_error");
        }
    };

    if (relationData === null || initialData === null) {
        return <Loading />;
    }

    let content: React.JSX.Element[];
    if (resourceDescriptor.tabbedOrders?.[action]) {
        const tabs = resourceDescriptor.tabbedOrders[action];
        content = [
            <Grid size={12} key="tabs">
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} aria-label="form tabs">
                        {tabs.map((tab, index) => (
                            // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
                            <Tab key={`tab-${index}`} label={<FormattedMessage id={tab.label} />} />
                        ))}
                    </Tabs>
                </Box>
                <Box sx={{ py: 1 }}>
                    {tabs.map((tab, index) => (
                        <Box
                            key={index}
                            hidden={activeTab !== index}
                            sx={{ display: activeTab === index ? "block" : "none" }}
                        >
                            <Grid container spacing={2} alignItems="center">
                                {createFieldRows(
                                    tab.fields,
                                    schema,
                                    resourceName,
                                    resourceDescriptor,
                                    action,
                                    control,
                                    errors,
                                    relationData,
                                    formValues,
                                )}
                            </Grid>
                        </Box>
                    ))}
                </Box>
            </Grid>,
        ];
    } else {
        content = createFieldRows(
            fieldNames,
            schema,
            resourceName,
            resourceDescriptor,
            action,
            control,
            errors,
            relationData,
            formValues,
        );
    }

    const hasErrors = Object.keys(errors).length > 0;

    return (
        <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} maxWidth="60ch">
            <Grid container spacing={2} alignItems="center">
                {...content}
                {serverError && (
                    <Grid size={12} justifyContent="flex-end">
                        <Alert severity="error">
                            {/* eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id */}
                            <FormattedMessage id={serverError} />
                        </Alert>
                    </Grid>
                )}
                <Grid size={12} container justifyContent="flex-end" spacing={1}>
                    {extraButtons &&
                        extraButtons.map((ExtraButton, index) => (
                            <Grid key={index}>
                                <ExtraButton formValues={formValues} />
                            </Grid>
                        ))}
                    <Grid>
                        <Button type="submit" disabled={hasErrors || !isDirty}>
                            <FormattedMessage id="form.save" defaultMessage="Save" />
                        </Button>
                    </Grid>
                    <Grid>
                        <Button onClick={handleReset} disabled={!isDirty}>
                            <FormattedMessage id="form.reset" defaultMessage="Reset" />
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CRUDForm;
